import { useRef } from 'react';
import { motion, useScroll, useTransform, useMotionTemplate } from 'framer-motion';
import { Section, GridContainer } from '../layout';
import { AnimatedSection } from '../shared';
import { Button } from '../ui';
import { analytics } from '../../../lib/analytics';

function PlusOperationsIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M40 16.116H23.884V0H16.116V16.116H0V23.884H16.116H22.978V24.818L16.116 29.676V40H23.884V27.05V24.176V23.884H40V16.116Z"
        fill="#FFFFFF"
      />
    </svg>
  );
}

function LessErrorsIcon() {
  return (
    <svg width="40" height="41" viewBox="0 0 40 41" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M24.6196 24.6616L23.9016 23.9436V0H16.0657V25.3836L5.54391 14.8698L0 20.4117L14.4378 34.8494L16.0657 36.4714L19.9817 40.3874H19.9857L23.9016 36.4714V25.3836V25.3796L24.6196 24.6616Z"
        fill="#FFFFFF"
      />
      <path
        d="M34.4565 14.8302L23.9023 25.3843L29.446 30.9279L40.0001 20.3738L34.4565 14.8302Z"
        fill="#FFFFFF"
      />
    </svg>
  );
}

function MoreControlIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10.64 10.22H20.4081H30.1761V0H20.4081H10.64H10.568L0 10.068V10.22V19.8261V30.1901H10.64V19.8261V10.22Z"
        fill="#FFFFFF"
      />
      <path
        d="M30.1767 10.2202V19.8263V30.1903H20.4087H10.6406V40.0003H20.4087H29.6767L30.1767 39.5243L39.9727 30.1903L40.0007 30.1643V19.8263V10.2202H30.1767Z"
        fill="#FFFFFF"
      />
    </svg>
  );
}

const outcomes = [
  { Icon: PlusOperationsIcon, line1: 'Más', line2: 'operaciones', label: 'Más operaciones' },
  { Icon: LessErrorsIcon, line1: 'Menos', line2: 'errores', label: 'Menos errores' },
  { Icon: MoreControlIcon, line1: 'Más', line2: 'control', label: 'Más control' },
];

export function CtaResult() {
  const sectionRef = useRef<HTMLElement>(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  // Top fade: shrinks as we scroll in (solid area reveals from top)
  const topFadeEnd = useTransform(scrollYProgress, [0, 0.35], [50, 8]);
  // Bottom fade: grows as we scroll out (solid area retreats upward)
  const bottomFadeStart = useTransform(scrollYProgress, [0.65, 1], [92, 50]);
  // Overall opacity: 0 when fully out of view, 1 when in range
  const bgOpacity = useTransform(scrollYProgress, [0, 0.15, 0.85, 1], [0, 1, 1, 0]);

  const maskImage = useMotionTemplate`linear-gradient(to bottom, transparent 0%, black ${topFadeEnd}%, black ${bottomFadeStart}%, transparent 100%)`;

  return (
    <Section ref={sectionRef} size="md" className="relative z-10">
      {/* Background overlay — extends past dashed dividers */}
      <motion.div
        className="absolute -inset-y-16 inset-x-0"
        style={{
          backgroundColor: '#E7EDF5',
          opacity: bgOpacity,
          maskImage,
          WebkitMaskImage: maskImage,
        }}
      />

      {/* Content */}
      <div className="relative">
        <AnimatedSection>
          <GridContainer>
            <div className="col-span-4 md:col-span-8 xl:col-span-12 text-center">
              <span className="text-accent text-sm font-medium tracking-wide uppercase">
                THE RESULT
              </span>
              <h2 className="mt-4 text-h1-mobile md:text-h1-tablet xl:text-h1 text-text-primary">
                <span className="block">Más capacidad.</span>
                <span className="block">Más previsibilidad.</span>
                <span className="block">Mismo equipo.</span>
              </h2>
              <p className="mt-6 text-body text-text-secondary max-w-lg mx-auto">
                <span className="md:block">
                  El incentivo fiscal deja de depender de herramientas aisladas
                </span>{' '}
                <span className="md:block">
                  y pasa a formar parte de una infraestructura operativa.
                </span>
              </p>

              {/* Outcomes grid */}
              <div className="mt-12 grid grid-cols-3 max-w-sm mx-auto md:flex md:items-center md:justify-center md:gap-24 md:max-w-none">
                {outcomes.map(({ Icon, line1, line2, label }) => (
                  <div key={label} className="flex flex-col items-center gap-2 md:flex-row">
                    <Icon />
                    <p className="text-xs xs:text-sm font-medium text-text-tertiary text-center md:text-left">
                      <span className="block sm:inline">{line1}</span>
                      <span className="hidden sm:inline"> </span>
                      <span className="block sm:inline">{line2}</span>
                    </p>
                  </div>
                ))}
              </div>

              {/* Book Demo CTA */}
              <div className="mt-12">
                <a href="/contact" onClick={() => analytics.ctaBookDemo()}>
                  <Button variant="primary">Book Demo</Button>
                </a>
              </div>
            </div>
          </GridContainer>
        </AnimatedSection>
      </div>
    </Section>
  );
}
